import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { listBookings, listContact } from "../api";
import { DESTINATIONS } from "../mock";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plane, ArrowLeft, Users, Mail } from "lucide-react";

const destName = (id) => DESTINATIONS.find((d) => d.id === id)?.name || id;

const Admin = () => {
  const { t } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    listBookings().then(setBookings).catch(() => {});
    listContact().then(setMessages).catch(() => {});
  }, []);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-teal-50/40">
      <header className="bg-[#0d3b43] text-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center">
              <Plane className="w-5 h-5 -rotate-12" />
            </span>
            <span className="font-display font-extrabold text-lg">
              {t("adminTitle")}
            </span>
          </div>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-white/80 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <Tabs defaultValue="bookings">
          <TabsList className="mb-6">
            <TabsTrigger value="bookings" className="gap-2">
              <Users className="w-4 h-4" /> {t("adminBookings")} ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="gap-2">
              <Mail className="w-4 h-4" /> {t("adminMessages")} ({messages.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <div className="bg-white rounded-2xl rc-shadow border border-teal-50 overflow-x-auto">
              {bookings.length === 0 ? (
                <p className="p-8 text-center text-slate-400">{t("noData")}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-teal-50 text-teal-800 text-left">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Ref</th>
                      <th className="px-4 py-3 font-semibold">{t("fullName")}</th>
                      <th className="px-4 py-3 font-semibold">{t("destination")}</th>
                      <th className="px-4 py-3 font-semibold">Hotel</th>
                      <th className="px-4 py-3 font-semibold">{t("passengers")}</th>
                      <th className="px-4 py-3 font-semibold">Vuelo</th>
                      <th className="px-4 py-3 font-semibold">{t("total")}</th>
                      <th className="px-4 py-3 font-semibold">Email</th>
                      <th className="px-4 py-3 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.id} className="border-t border-teal-50 hover:bg-teal-50/40">
                        <td className="px-4 py-3 font-mono text-teal-700">
                          {b.id.slice(0, 8).toUpperCase()}
                        </td>
                        <td className="px-4 py-3 font-semibold text-[#0d3b43]">
                          {b.customer_name}
                        </td>
                        <td className="px-4 py-3">{destName(b.destination_id)}</td>
                        <td className="px-4 py-3 text-slate-500">{b.hotel || "-"}</td>
                        <td className="px-4 py-3">{b.passengers}</td>
                        <td className="px-4 py-3 font-mono text-slate-500">{b.flight_number || "-"}</td>
                        <td className="px-4 py-3 font-bold">${b.price_usd} USD</td>
                        <td className="px-4 py-3 text-slate-500">{b.email}</td>
                        <td className="px-4 py-3 text-slate-400">{fmt(b.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <div className="bg-white rounded-2xl rc-shadow border border-teal-50 overflow-x-auto">
              {messages.length === 0 ? (
                <p className="p-8 text-center text-slate-400">{t("noData")}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-teal-50 text-teal-800 text-left">
                    <tr>
                      <th className="px-4 py-3 font-semibold">{t("fullName")}</th>
                      <th className="px-4 py-3 font-semibold">Email</th>
                      <th className="px-4 py-3 font-semibold">{t("phone")}</th>
                      <th className="px-4 py-3 font-semibold">{t("yourMessage")}</th>
                      <th className="px-4 py-3 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((m) => (
                      <tr key={m.id} className="border-t border-teal-50 hover:bg-teal-50/40">
                        <td className="px-4 py-3 font-semibold text-[#0d3b43]">{m.name}</td>
                        <td className="px-4 py-3 text-slate-500">{m.email}</td>
                        <td className="px-4 py-3 text-slate-500">{m.phone || "-"}</td>
                        <td className="px-4 py-3 max-w-md">{m.message}</td>
                        <td className="px-4 py-3 text-slate-400">{fmt(m.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Admin;
